import { z } from "zod";
import { MaterialRequestLineId } from "../fsm/fsm.schema.ts";

/**
 * Declared effect of an FSM transition on `shipped_quantity` of the material
 * lines of the request it runs on (BEL-646).
 *
 * The effect is configuration stored in `core_transition_material_effects`,
 * keyed by `transition_id` — never derived from the transition's `name` (which
 * is editable per customer) nor from `is_final` (a cancellation state is final
 * too). Same shape as `core_transition_bike_effects`.
 *
 * - `fill_to_total`: set `shipped_quantity = quantity` on every active line.
 * - `add_increment`: add the caller-declared per-line increment to
 *   `shipped_quantity`, never overwriting it.
 *
 * A transition with no row (e.g. `Cancelar`) never touches `shipped_quantity`.
 */
export const MaterialShippedQuantityEffect = z.enum([
  "fill_to_total",
  "add_increment",
]);
export type MaterialShippedQuantityEffect = z.infer<
  typeof MaterialShippedQuantityEffect
>;

/** Units shipped in THIS shipment for one line — an increment, not a total. */
export const MaterialShipmentIncrement = z.object({
  lineId: MaterialRequestLineId,
  increment: z.number().int().min(1),
});
export type MaterialShipmentIncrement = z.infer<
  typeof MaterialShipmentIncrement
>;

export const MaterialShipmentIncrements = z.array(MaterialShipmentIncrement);
export type MaterialShipmentIncrements = z.infer<
  typeof MaterialShipmentIncrements
>;

/** Units still owed on a line: requested minus already shipped, floored at 0. */
export function computePendingQuantity(line: {
  quantity: number;
  shippedQuantity: number;
}): number {
  return Math.max(0, line.quantity - line.shippedQuantity);
}
